import {createSlice, createAsyncThunk} from "@reduxjs/toolkit";

import config from '../../config'
import {get, post} from "../Api";
import * as cache from "../Cache";
import {getRoleMapping, isOwner} from "./AccountService";
import {MOCK_PRODUCT_CONFIG} from "../mock/MockProductConfig";

const initialState = {
    authenticated: false,
    initialized: false,
    account: null,
    name: null,
    roleMapping: null,
    isOwner: false,
    isSuccess: false,
    isError: false,
    errorMessage: '',
};

export const login = createAsyncThunk(
    'account/login',
    async ({credentials}, {rejectWithValue}) => {
        const LOGIN_ENDPOINT = `${config.services.ATLAS.apiUrl}/account/login`;
        const body = {
            email: credentials.email,
            password: credentials.password
        };
        try {
            const account = await post(false, LOGIN_ENDPOINT, body, {}, false);
            cache.set(config.constants.CACHE_KEY_TOKEN, account.token);
            cache.set(config.constants.CACHE_KEY_JWT, account.token);
            cache.set(config.constants.CACHE_KEY_API, account.token);
            cache.set(config.constants.CACHE_KEY_ACCOUNT, account );
            return account;
        } catch (err) {
            console.log(err);
            return rejectWithValue(err.response.body);
        }
    }
);

export const logout = createAsyncThunk(
    'account/logout',
    async () => {
        const LOGOUT_ENDPOINT = `${config.services.ATLAS.apiUrl}/account/logout`;
        await get(true, LOGOUT_ENDPOINT, {}, false);
    }
);



const getName = (account) => {
    let name = 'Rick Deckard';
    if (account.givenName && account.surName) name = `${account.givenName} ${account.surName}`;
    return name;
}

const accountSlice = createSlice({
    name: 'accountSlice',
    initialState,
    reducers: {
        clearState: (state) => {
            state.isError = false;
            state.isSuccess = false;
            state.errorMessage = '';

            return state;
        },
        getAccount: (state) => {
            const account = cache.get(config.constants.CACHE_KEY_ACCOUNT);

            if (account) {
                state.authenticated = true;
                state.initialized = true;
                state.account = account;
                state.name = getName(account);
                state.roleMapping = getRoleMapping(account);
                state.isOwner = isOwner(account);
                state.isSuccess = true;
            } else {
                state.authenticated = false;
                state.initialized = true;
                state.account = null;
                state.name = null;
                state.roleMapping = null;
                state.isOwner = false;
            }
            return state;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(login.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    const account = action.payload;

                    state.authenticated = true;
                    state.initialized = true;
                    state.account = account;
                    state.name = getName(account);
                    state.roleMapping = getRoleMapping(account);
                    state.isOwner = isOwner(account);
                    state.isSuccess = true;
                }
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                state.isError = true;
                if (action?.payload) {
                    let errObj = undefined;
                    try {
                        errObj = action?.payload;
                    } catch (e) {
                        // Don't need to do anything.
                    }
                    if (errObj) {
                        state.errorMessage = `${errObj.message || errObj.error.message}.`;
                    } else {
                        state.errorMessage = action.error.message;
                    }
                } else {
                    console.error(`There was an unhandled rejection`, action);
                }
            })
            .addCase(logout.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(logout.fulfilled, (state, action) => {
                state.loading = false;

                state.authenticated = false;
                state.initialized = false;
                state.account = null;
                state.name = null;
                state.roleMapping = null;
                state.isOwner = false;
                state.isSuccess = false;
                state.isError = false;
                state.errorMessage = '';

                cache.del(config.constants.CACHE_KEY_TOKEN);
                cache.del(config.constants.CACHE_KEY_ACCOUNT);
                cache.del(config.constants.CACHE_KEY_API);
                cache.del(config.constants.CACHE_KEY_JWT);
            })
            .addCase(logout.rejected, (state, action) => {
                state.loading = false;
                state.authenticated = false;
                state.initialized = false;
                state.account = null;
                state.name = null;
                state.roleMapping = null;
                state.isOwner = false;
                state.isSuccess = false;
                state.isError = false;
                state.errorMessage = '';
                cache.del(config.constants.CACHE_KEY_TOKEN);
                cache.del(config.constants.CACHE_KEY_ACCOUNT);
                cache.del(config.constants.CACHE_KEY_API);
                cache.del(config.constants.CACHE_KEY_JWT);
            });
    }
})


export const {clearState, getAccount} = accountSlice.actions;
export default accountSlice.reducer;
